<template>
  <RemotePaginatedList :hide-search="hideSearch"
                       :filter-options="filterOptions"
                       :headers="headers"
                       multi-sort
                       :show-select="showSelect"
                       v-model="selectedItemIds"
                       data-url="aafb/admin/bookings/"
                       data-key="bookings"
                       :extra-params="combinedExtraParams"
                       :refresh-trigger.sync="innerRefreshTrigger"
                       v-bind="$attrs">
    <template #item.reference_number="{value}">
      {{ value ? `BV${value.toString().padStart(10, "0")}` : $t('admin_acc_booking_add.maintenance_booking') }}
    </template>
    <template #item.hkiarace_account="{value}">
      {{ value ? $t('name', {given_name: value.given_name, surname: value.surname}) : '' }}
    </template>
    <template #item.create_at="{value}">
      {{ $parseDate(value) }}
    </template>
    <template #item.start_time="{value}">
      {{ $parseDate(value) }}
    </template>
    <template #item.end_time="{value}">
      {{ $parseDate(value) }}
    </template>
    <template #item.additional_items="{value}">
      <div v-for="item in value">
        {{ item.type.name }} x {{ item.count }}
      </div>
    </template>
    <template #item.status="{value}">
      {{ $t(BOOKING_STATUS_LABEL[value]) }}
    </template>
    <template #item.checkin_time="{value}">
      {{ value ? $parseDate(value) : '' }}
    </template>
    <template #item.checkout_time="{value}">
      {{ value ? $parseDate(value) : '' }}
    </template>
    <template v-slot:item.actions="{item}">
      <v-row v-if="!hideActions">
        <v-col v-if="!hideEdit">
          <v-tooltip top>
            <template #activator="{on, attrs}">
              <v-btn icon
                     v-on="on"
                     v-bind="attrs"
                     @click="editItem(item)">
                <v-icon>fas fa-search</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('edit') }}</span>
          </v-tooltip>
        </v-col>
        <v-col v-if="item.status === BOOKING_STATUS.CONFIRMED && !item.checkin_time">
          <v-tooltip top>
            <template #activator="{on, attrs}">
              <v-btn icon
                     v-on="on"
                     v-bind="attrs"
                     @click="cancelItem(item, false)">
                <v-icon>fas fa-trash</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('admin_acc_booking.cancel') }}</span>
          </v-tooltip>
        </v-col>
        <v-col v-if="item.status === BOOKING_STATUS.CONFIRMED">
          <v-tooltip top>
            <template #activator="{on, attrs}">
              <v-btn icon
                     v-on="on"
                     v-bind="attrs"
                     @click="cancelItem(item, true)">
                <v-icon>fas fa-hand-holding-usd</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('admin_acc_booking.refund') }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </template>
  </RemotePaginatedList>
</template>

<script>
import _ from 'lodash';
import {computed, onMounted, ref, watch} from '@vue/composition-api'
import RemotePaginatedList from "@/components/RemotePaginatedList";
import {BOOKING_STATUS, BOOKING_STATUS_LABEL} from "@/constants";

export default {
  name: 'AdminBookingList',
  components: {RemotePaginatedList},
  props: {
    value: Array,
    hideSearch: Boolean,
    showSelect: Boolean,
    refreshTrigger: Boolean,
    hideEdit: Boolean,
    hideCheckIn: Boolean,
    hideActions: Boolean,
    userId: String,
    itemId: String,
    extraParams: Object,
  },
  setup(props, {root, emit}) {
    const refresh = function () {
      innerRefreshTrigger.value = true;
    };
    onMounted(() => {
      getFacilityTypes();
      getRegions();
      refresh();
    });

    const combinedExtraParams = computed(() => {
      return {
        user_id: props.userId,
        item_id: props.itemId,
        ...props.extraParams,
      }
    })

    watch(() => props.refreshTrigger, (newValue) => {
      if (newValue) {
        refresh();
        emit('update:refresh-trigger', false);
      }
    })

    // Filter
    const facilityTypes = ref([]);
    const getFacilityTypes = function () {
      root.$callAPI('aafb/facility_types/', {}, function (response) {
        facilityTypes.value = response.body.item_types;
      });
    };
    const regions = ref([]);
    const getRegions = function () {
      root.$callAPI('room/region/', {}, function (response) {
        regions.value = response.body.regions;
      });
    }
    const filterOptions = computed(() => {
      return [
        {text: root.$t('admin_acc_booking.reference_number'), value: 'booking_record.aa_booking_record.reference_number', contains: true},
        {text: root.$t('admin_user.serial_no'), value: 'account.hkiarace_account.id', contains: true},
        {text: root.$t('admin_acc_booking.start_time'), value: 'start_time', date_range: true},
        {
          text: root.$t('admin_acc_booking.facility'), value: 'item.type.id',
          options: facilityTypes.value.map((t) => {
            return {text: t.name, value: t.id};
          }),
        },
        {
          text: root.$t('admin_acc_region.title'), value: 'item.region.id',
          options: regions.value.map((r) => {
            return {text: r.name, value: r.id};
          }),
        },
        {
          text: root.$t('admin_acc_booking.status'), value: 'status',
          options: Object.keys(BOOKING_STATUS_LABEL).map((k) => {
            return {text: root.$t(BOOKING_STATUS_LABEL[k]), value: k}
          })
        },
        {
          text: root.$t('admin_acc_booking.checkin_time'), value: 'checkin_time',
          options: [
            {text: root.$t('admin_acc_booking.not_checked_in'), value: false},
            {text: root.$t('admin_acc_booking.checked_in'), value: true},
          ], single: true,
        },
        {
          text: root.$t('admin_acc_booking.with_purchase_order'), value: 'purchase_order',
          options: [
            {text: root.$t('yes'), value: true},
            {text: root.$t('no'), value: false},
          ], single: true,
        },
      ]
    })

    // Data Table
    const headers = ref([
      {text: root.$t('admin_acc_booking.reference_number'), value: 'reference_number', sortable: false},
      {text: root.$t('admin_acc_booking.reserved_at'), value: 'create_at'},
      {text: root.$t('admin_user.serial_no'), value: 'hkiarace_account.serial_no', sortable: false},
      {text: root.$t('admin_acc_member.title'), value: 'hkiarace_account', sortable: false},
      {text: root.$t('admin_acc_booking.start_time'), value: 'start_time'},
      {text: root.$t('admin_acc_booking.end_time'), value: 'end_time'},
      {text: root.$t('admin_acc_booking.facility'), value: 'item.name'},
      {text: root.$t('admin_acc_facility_type.additional_items'), value: 'additional_items'},
      {text: root.$t('admin_acc_booking.status'), value: 'status'},
      {text: root.$t('admin_acc_booking.checkin_time'), value: 'checkin_time'},
      {text: root.$t('admin_acc_booking.checkout_time'), value: 'checkout_time'},

      {text: '', value: 'actions', sortable: false},
    ]);
    const innerRefreshTrigger = ref(false);

    const selectedItemIds = ref([]);
    watch(() => selectedItemIds.value, (newValue) => {
      emit('input', newValue);
    });

    watch(() => props.value, (newValue, oldValue) => {
      if (_.xor(newValue, oldValue).length) {
        selectedItemIds.value = newValue;
      }
    }, {deep: true, immediate: true})

    const editItem = function (item) {
      emit('edit', item);
    };
    const cancelItem = function (item, issueCoupon) {
      let msg = issueCoupon ? root.$t('admin_acc_booking.confirm_refund_msg') : root.$t('admin_acc_booking.confirm_cancel_msg');

      if (confirm(msg)) {
        root.$callAPI('aafb/admin/booking/cancel/', {
          booking_id: item.id, issue_coupon: issueCoupon
        }, () => {
          root.$showAlertMessage(root.$t('admin_acc_booking.cancel_success_msg'));
          refresh();
        });
      }
    };

    return {
      combinedExtraParams,

      filterOptions,

      headers,
      innerRefreshTrigger,
      selectedItemIds,

      BOOKING_STATUS,
      BOOKING_STATUS_LABEL,

      editItem,
      cancelItem,
    }
  }
}
</script>
